
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Coins, TrendingUp, Calendar, Award, Globe, Star } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { PointsService, UserPoints, PointTransaction, CountryPointsConfig, UserCountryVerification } from '@/services/points/PointsService';
import { formatDistanceToNow } from 'date-fns';
import CountryFlag from '@/components/country/CountryFlag';

const MultiCountryPointsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [verifiedCountries, setVerifiedCountries] = useState<UserCountryVerification[]>([]);
  const [allUserPoints, setAllUserPoints] = useState<UserPoints[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<PointTransaction[]>([]);
  const [countryConfigs, setCountryConfigs] = useState<Record<string, CountryPointsConfig>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadMultiCountryPointsData();
    } else {
      setIsLoading(false);
    }
  }, [user?.id]);

  const loadMultiCountryPointsData = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Loading multi-country points data for user:', user.id);
      
      // Get verified countries
      const countries = await PointsService.getUserVerifiedCountries(user.id);
      setVerifiedCountries(countries);

      if (countries.length === 0) {
        setIsLoading(false);
        return;
      }

      // Get points for all countries
      const allPoints = await PointsService.getAllUserPoints(user.id);
      setAllUserPoints(allPoints);

      // Load country configurations
      const configs: Record<string, CountryPointsConfig> = {};
      for (const country of countries) {
        const config = await PointsService.getCountryConfig(country.country_code);
        if (config) {
          configs[country.country_code] = config;
        }
      }
      setCountryConfigs(configs);

      // Set default selected country to primary or first country
      const primaryCountry = countries.find(c => c.primary_verification);
      const defaultCountry = primaryCountry?.country_code || countries[0]?.country_code;
      if (defaultCountry) {
        setSelectedCountry(defaultCountry);
        // Load transactions for the default country
        const defaultTransactions = await PointsService.getUserTransactions(user.id, defaultCountry, 10);
        setTransactions(defaultTransactions);
      }

    } catch (error) {
      console.error('Error loading multi-country points data:', error);
      setError('Failed to load points data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCountrySelect = async (countryCode: string) => {
    if (!user?.id) return;
    
    setSelectedCountry(countryCode);
    
    try {
      const countryTransactions = await PointsService.getUserTransactions(user.id, countryCode, 10);
      setTransactions(countryTransactions);
    } catch (error) {
      console.error('Error loading transactions for country:', countryCode, error);
    }
  };

  const handleSetPrimaryCountry = async (countryCode: string) => {
    if (!user?.id) return;

    try {
      const result = await PointsService.setPrimaryCountry(user.id, countryCode);
      if (result.success) {
        // Reload data to reflect changes
        await loadMultiCountryPointsData();
      } else {
        setError(result.error || 'Failed to set primary country');
      }
    } catch (error) {
      console.error('Error setting primary country:', error);
      setError('Failed to set primary country');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Multi-Country Points Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Multi-Country Points Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-red-500">
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (verifiedCountries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-gray-400" />
            Points Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-gray-500">
            <Award className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="font-medium">No Verified Countries</p>
            <p className="text-sm">Complete identity verification for a country to start earning points.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const selectedCountryPoints = allUserPoints.find(p => p.country_code === selectedCountry);
  const selectedCountryConfig = selectedCountry ? countryConfigs[selectedCountry] : null;

  return (
    <div className="space-y-4">
      {/* Country Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            Your Verified Countries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {verifiedCountries.map((country) => {
              const points = allUserPoints.find(p => p.country_code === country.country_code);
              const config = countryConfigs[country.country_code];
              
              return (
                <Card 
                  key={country.id} 
                  className={`cursor-pointer transition-all ${
                    selectedCountry === country.country_code 
                      ? 'ring-2 ring-blue-500 bg-blue-50' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleCountrySelect(country.country_code)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CountryFlag countryCode={country.country_code} className="h-5 w-6" />
                        <span className="font-medium">{country.country_code}</span>
                      </div>
                      {country.primary_verification && (
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      {country.constituency_code && (
                        <p>Constituency: {country.constituency_code}</p>
                      )}
                    </div>
                    {config?.points_enabled ? (
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">
                          {config.currency_symbol}{(points?.total_points || 0).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">Available Points</div>
                      </div>
                    ) : (
                      <div className="text-center text-sm text-gray-500">
                        Points not enabled
                      </div>
                    )}
                    {!country.primary_verification && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetPrimaryCountry(country.country_code);
                        }}
                      >
                        Set as Primary
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Country Details */}
      {selectedCountry && selectedCountryConfig?.points_enabled && (
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CountryFlag countryCode={selectedCountry} className="h-5 w-6" />
                  {selectedCountry} Points Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {selectedCountryConfig.currency_symbol}{(selectedCountryPoints?.total_points || 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">Current Balance</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {selectedCountryConfig.currency_symbol}{(selectedCountryPoints?.lifetime_earned || 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">Lifetime Earned</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {selectedCountryConfig.currency_symbol}{selectedCountryConfig.poll_reward.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">Per Poll Vote</div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Monthly Limit</span>
                    <span className="text-sm text-gray-600">
                      {selectedCountryConfig.currency_symbol}{selectedCountryConfig.monthly_limit.toFixed(2)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.min(((selectedCountryPoints?.lifetime_earned || 0) / selectedCountryConfig.monthly_limit) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Recent Activity - {selectedCountry}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <Award className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No transactions yet for {selectedCountry}</p>
                    <p className="text-sm">Participate in polls to start earning points!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            transaction.transaction_type === 'earned' 
                              ? 'bg-green-500' 
                              : 'bg-red-500'
                          }`}></div>
                          <div>
                            <div className="font-medium text-sm">{transaction.description}</div>
                            <div className="text-xs text-gray-500">
                              {formatDistanceToNow(new Date(transaction.created_at), { addSuffix: true })}
                            </div>
                          </div>
                        </div>
                        <Badge 
                          variant={transaction.transaction_type === 'earned' ? 'default' : 'destructive'}
                          className="ml-2"
                        >
                          {transaction.transaction_type === 'earned' ? '+' : '-'}
                          {selectedCountryConfig?.currency_symbol}{transaction.amount.toFixed(2)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Points Info */}
      {selectedCountryConfig?.points_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              How Points Work - {selectedCountry}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-gray-600">
            <p>• Earn {selectedCountryConfig.currency_symbol}{selectedCountryConfig.poll_reward.toFixed(2)} for each poll you vote on</p>
            <p>• Monthly earning limit: {selectedCountryConfig.currency_symbol}{selectedCountryConfig.monthly_limit.toFixed(2)}</p>
            <p>• Minimum redemption: {selectedCountryConfig.currency_symbol}{selectedCountryConfig.min_redemption_amount.toFixed(2)}</p>
            <p>• Points are country-specific and reset monthly</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MultiCountryPointsDashboard;
